import React from 'react'
import { Alert, Flex, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { PeriodQuery, PlatformOverview } from '../dashboard'
import { useAdminOverview } from '../hooks'
import { MetricTile } from './metric-tile'
import { DailyBarChart } from './daily-bar-chart'
import { CountChip } from '../../components/count-chip'
import { formatMicros } from './spend-tables'

type Metric = keyof PlatformOverview['metrics']
type Series = Exclude<keyof PlatformOverview['perDay'][number], 'date'>

export function AdminOverview(query: PeriodQuery) {
    const { data, isPending, isError } = useAdminOverview(query)
    const { t } = useTranslation()

    if (isError) {
        return (
            <Alert
                type="error"
                showIcon
                message={t('Could not load statistics for this period.')}
            />
        )
    }

    const format = (metric: Metric, value: number) =>
        metric === 'spendEurMicros' ? formatMicros(value, 'EUR') : value.toLocaleString()

    const tiles: { metric: Metric; label: string }[] = [
        { metric: 'companies', label: t('New companies') },
        { metric: 'subscriptions', label: t('New subscriptions') },
        { metric: 'users', label: t('New users') },
        { metric: 'spendEurMicros', label: t('Spend') },
        { metric: 'candidates', label: t('Candidates') },
        { metric: 'whatsappMessages', label: t('WhatsApp messages') },
        { metric: 'chats', label: t('Website chats') },
        { metric: 'visitorMessages', label: t('Chat messages') },
    ]

    const charts: { series: Series; label: string }[] = [
        { series: 'companies', label: t('New companies') },
        { series: 'candidates', label: t('Candidates') },
        { series: 'whatsappMessages', label: t('WhatsApp messages') },
        { series: 'chats', label: t('Website chats') },
    ]

    return (
        <Flex
            vertical
            gap="large"
        >
            <div className="overflow-hidden rounded-xl border border-hairline">
                <div className="grid grid-cols-2 gap-px bg-hairline md:grid-cols-4">
                    {tiles.map(({ metric, label }) => {
                        const comparison = data?.metrics[metric]

                        return (
                            <MetricTile
                                key={metric}
                                loading={isPending}
                                label={label}
                                value={comparison && format(metric, comparison.current)}
                                hint={
                                    comparison && (
                                        <Change
                                            current={comparison.current}
                                            previous={comparison.previous}
                                            formattedPrevious={format(metric, comparison.previous)}
                                        />
                                    )
                                }
                            />
                        )
                    })}
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {charts.map(({ series, label }) => (
                    <div
                        key={series}
                        className="min-w-0 rounded-xl border border-hairline bg-white p-5"
                    >
                        <Flex
                            align="center"
                            gap={8}
                            className="mb-4"
                        >
                            <Typography.Title
                                level={5}
                                className="mb-0"
                            >
                                {label}
                            </Typography.Title>
                            <CountChip value={data?.perDay.reduce((sum, day) => sum + day[series], 0)} />
                        </Flex>
                        <DailyBarChart
                            data={data?.perDay}
                            dataKey={series}
                            label={label}
                        />
                    </div>
                ))}
            </div>
        </Flex>
    )
}

// By the sign of the change.
const ARROWS: Record<number, string> = {
    1: '↑',
    0: '→',
    [-1]: '↓',
}

type ChangeProps = {
    current: number
    previous: number
    formattedPrevious: string
}

// Neutral ink on purpose: more is good for candidates and bad for spend, so the arrow carries the direction.
function Change({ current, previous, formattedPrevious }: ChangeProps) {
    const { t } = useTranslation()

    if (previous === 0) {
        return current === 0 ? t('No change from the previous period') : t('Up from 0 in the previous period')
    }

    const percentage = Math.round(((current - previous) / previous) * 100)
    const arrow = ARROWS[Math.sign(percentage)]

    return t('{{arrow}} {{percentage}}% from {{previous}} in the previous period', {
        arrow,
        percentage: Math.abs(percentage),
        previous: formattedPrevious,
    })
}
